import React, { createContext, useContext, useState, useCallback, useRef, ReactNode, useEffect } from 'react';
import { logManualAiSpend } from '@/lib/aiSpendLedger';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { db } from '@/lib/supabaseQuery';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import i18n from '@/i18n/config';
import { useTranslation } from 'react-i18next';
import { isProblematicValue } from '@/lib/localizationUtils';
import { useAIModels } from '@/hooks/useAIModels';
import { useLocaleConfigs } from '@/hooks/useLocaleConfigs';
export interface EditLocaleOption {
  code: string;
  name: string;
  isSource: boolean;
}

export interface LocalizationKey {
  namespace: string;
  key: string;
  currentValue: string;
  scope: 'page' | 'header' | 'footer' | 'sidebar';
}

export interface LocalizationSuggestion {
  original: string;
  suggested: string;
  confidence: number;
}


interface LocalizationEditContextType {
  isEditMode: boolean;
  currentScope: 'page' | 'header' | 'footer' | 'sidebar' | 'all';
  visibleKeys: Map<string, LocalizationKey>;
  hiddenKeys: Map<string, LocalizationKey>;
  pendingEdits: Map<string, string>;
  suggestions: Map<string, LocalizationSuggestion>;
  selectedModel: string;
  isRegenerating: boolean;
  regeneratingKey: string | null;
  isSaving: boolean;
  isLoadingDb: boolean;
  pageRoute: string;
  pageTitle: string;
  showProblematicOnly: boolean;
  setShowProblematicOnly: (value: boolean) => void;
  problematicCount: number;
  
  // Locale selector
  editLocale: string;
  setEditLocale: (locale: string) => void;
  availableLocales: EditLocaleOption[];
  
  // Actions
  enterEditMode: () => Promise<void>;
  exitEditMode: () => void;
  setCurrentScope: (scope: 'page' | 'header' | 'footer' | 'sidebar' | 'all') => void;
  registerKey: (namespace: string, key: string, value: string, scope: 'page' | 'header' | 'footer' | 'sidebar') => void;
  unregisterKey: (namespace: string, key: string) => void;
  registerHiddenKey: (namespace: string, key: string, value: string) => void;
  setPendingEdit: (compositeKey: string, value: string) => void;
  clearPendingEdit: (compositeKey: string) => void;
  setSelectedModel: (model: string) => void;
  regeneratePage: () => Promise<number>;
  regenerateSingleKey: (namespace: string, key: string, currentValue: string) => Promise<LocalizationSuggestion | null>;
  applySuggestion: (compositeKey: string) => void;
  rejectSuggestion: (compositeKey: string) => void;
  applyAllSuggestions: () => void;
  rejectAllSuggestions: () => void;
  saveAllChanges: () => Promise<void>;
  discardChanges: () => void;
  getPendingCount: () => number;
  getDbValue: (namespace: string, key: string) => string | undefined;
}

// NOTE: Localization audit prompt is managed in ai_agent_prompts table (agent_id: 'localization-ui-audit')

const LocalizationEditContext = createContext<LocalizationEditContextType | null>(null);

export function LocalizationEditProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const { user } = useAuth();
  const { t: lCommon } = useTranslation('common');
  
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentScope, setCurrentScope] = useState<'page' | 'header' | 'footer' | 'sidebar' | 'all'>('all');
  const [visibleKeys, setVisibleKeys] = useState<Map<string, LocalizationKey>>(new Map());
  const [hiddenKeys, setHiddenKeys] = useState<Map<string, LocalizationKey>>(new Map());
  const [pendingEdits, setPendingEdits] = useState<Map<string, string>>(new Map());
  const [suggestions, setSuggestions] = useState<Map<string, LocalizationSuggestion>>(new Map());
  const [dbTranslations, setDbTranslations] = useState<Map<string, string>>(new Map());
  const dbTranslationsRef = useRef<Map<string, string>>(new Map());
  const [isLoadingDb, setIsLoadingDb] = useState(false);
  const { defaultModel } = useAIModels();
  const [selectedModel, setSelectedModel] = useState(() => 
    localStorage.getItem('localization_edit_model') || ''
  );
  // customPrompt removed - prompt is now managed in ai_agent_prompts table
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [regeneratingKey, setRegeneratingKey] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [showProblematicOnly, setShowProblematicOnly] = useState(true); // Default to problematic only
  const [allRouteKeys, setAllRouteKeys] = useState<Map<string, LocalizationKey>>(new Map());
  
  // Locale selector state
  const [editLocale, setEditLocaleState] = useState<string>('en');
  const { data: allLocales } = useLocaleConfigs(); // Gets all enabled locales (primary + regional)
  
  // Build available locales list with EN Source first
  const availableLocales: EditLocaleOption[] = React.useMemo(() => {
    const sourceLocale: EditLocaleOption = { 
      code: 'en', 
      name: 'English (Source)', 
      isSource: true 
    };
    
    if (!allLocales) {
      return [sourceLocale];
    }
    
    const otherLocales = allLocales
      .filter(l => l.locale !== 'en' && l.isEnabled)
      .map(l => ({
        code: l.locale,
        name: l.englishDisplayName,
        isSource: false,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
    
    return [sourceLocale, ...otherLocales];
  }, [allLocales]);

  // Get relevant namespace prefixes based on current route
  const getRelevantNamespacePrefixes = useCallback((pathname: string): string[] => {
    const segments = pathname.split('/').filter(Boolean);
    const prefixes: string[] = ['common.']; // Always include common namespace
    
    // Reserved paths that are NOT location pages
    const reservedPaths = ['locations', 'experts', 'auth', 'account', 'admin', 'help-center', 'resources', 'strategies', 'about-us', 'methodology', 'demo'];
    
    if (segments.length === 0) {
      // Homepage
      prefixes.push('pages.home.');
      prefixes.push('components.hero');
      prefixes.push('components.featured');
      prefixes.push('components.inquiry_suggestions');
      prefixes.push('components.chat');
      prefixes.push('components.expert_card');
      prefixes.push('components.footer');
      prefixes.push('components.nav');
    } else if (segments[0] === 'admin') {
      prefixes.push('admin.');
      if (segments[1]) {
        prefixes.push(`admin.${segments[1]}.`);
      }
      prefixes.push('components.admin');
    } else if (segments[0] === 'account') {
      prefixes.push('pages.account.');
      prefixes.push('pages.dashboard.');
      prefixes.push('pages.publications.');
      prefixes.push('components.account');
      prefixes.push('components.membership');
      prefixes.push('components.notification');
      prefixes.push('components.locale');
      prefixes.push('components.profile');
      prefixes.push('components.practice_areas');
      prefixes.push('components.opportunities');
      prefixes.push('components.publication');
      if (segments[1]) {
        const subPage = segments[1].replace(/-/g, '_');
        prefixes.push(`pages.${subPage}.`);
        prefixes.push(`components.${subPage}`);
        // Dashboard has opportunities
        if (subPage === 'dashboard') {
          prefixes.push('pages.dashboard.opportunities.');
          prefixes.push('pages.dashboard.content.');
          prefixes.push('pages.dashboard.metrics.');
          prefixes.push('pages.dashboard.reputation.');
        }
      }
    } else if (segments[0] === 'auth') {
      prefixes.push('auth.');
      prefixes.push('pages.auth.');
    } else if (segments[0] === 'support') {
      prefixes.push('pages.support.');
      prefixes.push('components.support');
    } else if (segments[0] === 'experts') {
      prefixes.push('pages.experts.');
      prefixes.push('pages.expert_profile.');
      prefixes.push('components.expert');
    } else if (segments[0] === 'locations') {
      prefixes.push('pages.locations.');
      prefixes.push('components.location');
    } else if (segments.length === 1 && !reservedPaths.includes(segments[0])) {
      // Dynamic location pages like /united-states, /españa
      prefixes.push('pages.locations.');
      prefixes.push('pages.home.');
      prefixes.push('components.hero');
      prefixes.push('components.featured');
      prefixes.push('components.inquiry_suggestions');
      prefixes.push('components.chat');
      prefixes.push('components.expert_card');
      prefixes.push('components.location');
    } else if (segments.length === 2 && !reservedPaths.includes(segments[0])) {
      // Expert profile pages like /vanity-url/publication-slug
      prefixes.push('pages.expert_profile.');
      prefixes.push('components.expert');
      prefixes.push('components.publication');
    } else {
      // Other public pages
      const pageName = segments[0].replace(/-/g, '_');
      prefixes.push(`pages.${pageName}.`);
      prefixes.push(`components.${pageName}`);
      prefixes.push(`components.expert`);
      prefixes.push(`components.publication`);
    }
    
    // Always include navigation components
    prefixes.push('components.nav');
    prefixes.push('components.footer');
    prefixes.push('components.header');
    
    return prefixes;
  }, []);

  // Handle locale change with pending edits warning
  const setEditLocale = useCallback((newLocale: string) => {
    if (pendingEdits.size > 0) {
      if (!confirm('You have unsaved changes. Switching locales will discard them. Continue?')) {
        return;
      }
      setPendingEdits(new Map());
      setSuggestions(new Map());
    }
    setEditLocaleState(newLocale);
  }, [pendingEdits.size]);

  // Load localizations from database and pre-register route-relevant keys as hidden
  interface LocalizationDbRow {
    namespace: string;
    key: string;
    value: string;
  }
  
  const loadLocalizationsFromDb = useCallback(async (pathname: string, targetLocale?: string) => {
    setIsLoadingDb(true);
    const languageToFetch = targetLocale || editLocale;
    try {
      const allLocalizations = await db.fetchAll<LocalizationDbRow>(
        'localization_user_interface',
        'namespace, key, value',
        { filters: [{ column: 'language', operator: 'eq', value: languageToFetch }] }
      );
      
      // Build map: "namespace.key" -> value (full database for lookups)
      const transMap = new Map<string, string>();
      for (const t of allLocalizations) {
        transMap.set(`${t.namespace}.${t.key}`, t.value);
      }
      
      // Update both state and ref for immediate access
      dbTranslationsRef.current = transMap;
      setDbTranslations(transMap);
      
      // Filter keys by route-relevant namespace prefixes
      const relevantPrefixes = getRelevantNamespacePrefixes(pathname);
      const newAllRouteKeys = new Map<string, LocalizationKey>();
      
      for (const t of allLocalizations) {
        const compositeKey = `${t.namespace}.${t.key}`;
        // Check if key matches any relevant prefix
        const isRelevant = relevantPrefixes.some(prefix => compositeKey.startsWith(prefix));
        
        if (isRelevant) {
          newAllRouteKeys.set(compositeKey, {
            namespace: t.namespace,
            key: t.key,
            currentValue: t.value,
            scope: 'page' as const,
          });
        }
      }
      setAllRouteKeys(newAllRouteKeys);
      
      // Apply problematic filter for hiddenKeys
      const newHiddenKeys = new Map<string, LocalizationKey>();
      newAllRouteKeys.forEach((tk, compositeKey) => {
        if (isProblematicValue(tk.key, tk.currentValue)) {
          newHiddenKeys.set(compositeKey, tk);
        }
      });
      setHiddenKeys(newHiddenKeys);
    } catch {
      toast.error(lCommon('toast.failed_to_load_translations.error', { defaultValue: 'Failed to load translations' }));
    } finally {
      setIsLoadingDb(false);
    }
  }, [getRelevantNamespacePrefixes, editLocale]);

  // Get value from database cache - use ref to avoid stale closure
  const getDbValue = useCallback((namespace: string, key: string): string | undefined => {
    return dbTranslationsRef.current.get(`${namespace}.${key}`);
  }, []);

  const enterEditMode = useCallback(async () => {
    // Keep current language - edit mode respects selected language
    setIsEditMode(true);
    setPageTitle(document.title);
    // Load localizations from database filtered by current route
    await loadLocalizationsFromDb(location.pathname);
  }, [loadLocalizationsFromDb, location.pathname]);

  const exitEditMode = useCallback(() => {
    if (pendingEdits.size > 0 || suggestions.size > 0) {
      if (!confirm(lCommon('messages.unsaved_changes_confirm', { defaultValue: 'You have unsaved changes. Discard?' }))) {
        return;
      }
    }
    setIsEditMode(false);
    setVisibleKeys(new Map());
    setHiddenKeys(new Map());
    setPendingEdits(new Map());
    setSuggestions(new Map());
    setDbTranslations(new Map());
    dbTranslationsRef.current = new Map();
  }, [pendingEdits.size, suggestions.size]);

  // SIMPLIFIED: Removed auto-enter edit mode based on source mode
  // Edit mode is now explicitly entered via LocalizationSettingsCard or the floating panel


  // Reload translations when editLocale changes while in edit mode
  useEffect(() => {
    if (isEditMode) {
      loadLocalizationsFromDb(location.pathname);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editLocale]);

  const registerKey = useCallback((namespace: string, key: string, value: string, scope: 'page' | 'header' | 'footer' | 'sidebar') => {
    const compositeKey = `${namespace}.${key}`;
    setVisibleKeys(prev => {
      const next = new Map(prev);
      next.set(compositeKey, { namespace, key, currentValue: value, scope });
      return next;
    });
    // Remove from hidden keys when it becomes visible (avoid duplicates)
    setHiddenKeys(prev => {
      if (prev.has(compositeKey)) {
        const next = new Map(prev);
        next.delete(compositeKey);
        return next;
      }
      return prev;
    });
  }, []);

  const unregisterKey = useCallback((namespace: string, key: string) => {
    const compositeKey = `${namespace}.${key}`;
    setVisibleKeys(prev => {
      const next = new Map(prev);
      next.delete(compositeKey);
      return next;
    });
  }, []);

  const registerHiddenKey = useCallback((namespace: string, key: string, value: string) => {
    const compositeKey = `${namespace}.${key}`;
    setHiddenKeys(prev => {
      const next = new Map(prev);
      next.set(compositeKey, { namespace, key, currentValue: value, scope: 'page' });
      return next;
    });
  }, []);

  const setPendingEdit = useCallback((compositeKey: string, value: string) => {
    setPendingEdits(prev => {
      const next = new Map(prev);
      next.set(compositeKey, value);
      return next;
    });
  }, []);

  const clearPendingEdit = useCallback((compositeKey: string) => {
    setPendingEdits(prev => {
      const next = new Map(prev);
      next.delete(compositeKey);
      return next;
    });
  }, []);

  // setCustomPrompt removed - prompt is now managed in ai_agent_prompts table

  const handleSetSelectedModel = useCallback((model: string) => {
    setSelectedModel(model);
    localStorage.setItem('localization_edit_model', model);
  }, []);

  const regeneratePage = useCallback(async (): Promise<number> => {
    setIsRegenerating(true);
    setSuggestions(new Map()); // Clear old suggestions before new API call
    
    try {
      // Collect all keys based on current scope
      const keysToRegenerate: Array<{ namespace: string; key: string; currentValue: string; scope: string }> = [];
      
      const addKeys = (keyMap: Map<string, LocalizationKey>) => {
        keyMap.forEach((tk, compositeKey) => {
          const scopeMatch = currentScope === 'all' || tk.scope === currentScope;
          // When problematic filter is on, only include problematic values
          const isProblematic = !showProblematicOnly || isProblematicValue(compositeKey, tk.currentValue);
          if (scopeMatch && isProblematic) {
            keysToRegenerate.push({
              namespace: tk.namespace,
              key: tk.key,
              currentValue: pendingEdits.get(compositeKey) || tk.currentValue,
              scope: tk.scope,
            });
          }
        });
      };
      
      addKeys(visibleKeys);
      addKeys(hiddenKeys);
      
      if (keysToRegenerate.length === 0) {
        toast.info(lCommon('toast.no_keys_to_regenerate', { defaultValue: 'No keys to regenerate' }));
        return 0;
      }

      // UI-triggered AI fan-out: record the click in ai_batch_ledger, the
      // database spend gate does not observe browser-invoked calls.
      await logManualAiSpend({
        functionName: 'localization-ui-audit-en',
        requestCount: keysToRegenerate.length,
        entityType: 'ui_string',
        displayName: pageTitle || location.pathname,
      });

      const response = await supabase.functions.invoke('localization-ui-audit-en', {
        body: {
          keys: keysToRegenerate,
          pageRoute: location.pathname,
          pageTitle,
          model: selectedModel,
        },
      });

      if (response.error) throw response.error;

      // Process JSON response (SSE removed - now JSON only)
      if (response.data?.suggestions) {
        const newSuggestions = new Map<string, LocalizationSuggestion>();
        interface SuggestionResponse {
          namespace: string;
          key: string;
          original: string;
          suggested: string;
          confidence: number;
        }
        response.data.suggestions.forEach((s: SuggestionResponse) => {
          newSuggestions.set(`${s.namespace}.${s.key}`, {
            original: s.original,
            suggested: s.suggested,
            confidence: s.confidence,
          });
        });
        setSuggestions(newSuggestions);
        toast.success(lCommon('toast.suggestions_generated.success', { count: newSuggestions.size, defaultValue: `${newSuggestions.size} suggestions generated` }));
        return newSuggestions.size;
      }
      
      return 0;
    } catch {
      toast.error(lCommon('toast.failed_to_regenerate.error', { defaultValue: 'Failed to regenerate' }));
      return 0;
    } finally {
      setIsRegenerating(false);
    }
  }, [visibleKeys, hiddenKeys, currentScope, pendingEdits, location.pathname, pageTitle, selectedModel]);

  const regenerateSingleKey = useCallback(async (
    namespace: string,
    key: string,
    currentValue: string
  ): Promise<LocalizationSuggestion | null> => {
    const compositeKey = `${namespace}.${key}`;
    setRegeneratingKey(compositeKey);
    
    try {
      await logManualAiSpend({
        functionName: 'localization-ui-audit-en',
        requestCount: 1,
        entityType: 'ui_string',
        displayName: compositeKey,
      });

      const response = await supabase.functions.invoke('localization-ui-audit-en', {
        body: {
          keys: [{ namespace, key, currentValue, scope: 'page' }],
          pageRoute: location.pathname,
          pageTitle,
          model: selectedModel,
        },
      });

      if (response.error) throw response.error;

      // Handle non-streaming response
      const suggestion = response.data?.suggestions?.[0];
      if (suggestion) {
        const suggestionObj: LocalizationSuggestion = {
          original: suggestion.original,
          suggested: suggestion.suggested,
          confidence: suggestion.confidence,
        };
        
        // Store in suggestions map for consistency
        setSuggestions(prev => {
          const next = new Map(prev);
          next.set(compositeKey, suggestionObj);
          return next;
        });
        
        return suggestionObj;
      }
      
      toast.info(lCommon('toast.no_improvements_found', { defaultValue: 'No improvements found' }));
      return null;
    } catch {
      toast.error(lCommon('toast.failed_to_regenerate.error', { defaultValue: 'Failed to regenerate' }));
      return null;
    } finally {
      setRegeneratingKey(null);
    }
  }, [location.pathname, pageTitle, selectedModel]);

  const applySuggestion = useCallback((compositeKey: string) => {
    const suggestion = suggestions.get(compositeKey);
    if (suggestion) {
      setPendingEdits(prev => {
        const next = new Map(prev);
        next.set(compositeKey, suggestion.suggested);
        return next;
      });
      setSuggestions(prev => {
        const next = new Map(prev);
        next.delete(compositeKey);
        return next;
      });
    }
  }, [suggestions]);

  const rejectSuggestion = useCallback((compositeKey: string) => {
    setSuggestions(prev => {
      const next = new Map(prev);
      next.delete(compositeKey);
      return next;
    });
  }, []);

  const applyAllSuggestions = useCallback(() => {
    suggestions.forEach((suggestion, compositeKey) => {
      setPendingEdits(prev => {
        const next = new Map(prev);
        next.set(compositeKey, suggestion.suggested);
        return next;
      });
    });
    setSuggestions(new Map());
  }, [suggestions]);

  const rejectAllSuggestions = useCallback(() => {
    setSuggestions(new Map());
  }, []);

  const saveAllChanges = useCallback(async () => {
    if (pendingEdits.size === 0) {
      toast.info(lCommon('toast.no_changes_to_save', { defaultValue: 'No changes to save' }));
      return;
    }
    
    setIsSaving(true);
    
    try {
      const updates: Array<{ namespace: string; key: string; value: string }> = [];
      
      pendingEdits.forEach((value, compositeKey) => {
        const [namespace, ...keyParts] = compositeKey.split('.');
        updates.push({
          namespace,
          key: keyParts.join('.'),
          value,
        });
      });
      
      // Upsert each translation in the database (insert if missing, update if exists)
      for (const update of updates) {
        const { error } = await supabase
          .from('localization_user_interface')
          .upsert({
            namespace: update.namespace,
            key: update.key,
            language: editLocale, // Use selected locale instead of hardcoded 'en'
            value: update.value,
            generated_by_ai: false,
            // qa-bypass: human-approved (admin user explicit save action)
            needs_review: false,
            intentionally_kept: true,
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
          }, { onConflict: 'namespace,key,language' });
          
        if (error) {
          throw error;
        }
      }
      
      // Update local dbTranslations cache with saved values
      setDbTranslations(prev => {
        const next = new Map(prev);
        pendingEdits.forEach((value, compositeKey) => {
          next.set(compositeKey, value);
        });
        dbTranslationsRef.current = next;
        return next;
      });
      
      // Reload i18n resources
      await i18n.reloadResources('en-US');
      
      toast.success(lCommon('toast.translations_saved.success', { count: updates.length, defaultValue: `${updates.length} translations saved` }));
      setPendingEdits(new Map());
    } catch {
      toast.error(lCommon('toast.failed_to_save_translations.error', { defaultValue: 'Failed to save translations' }));
    } finally {
      setIsSaving(false);
    }
  }, [pendingEdits, user?.id, editLocale]);

  const discardChanges = useCallback(() => {
    setPendingEdits(new Map());
    setSuggestions(new Map());
  }, []);

  const getPendingCount = useCallback(() => {
    return pendingEdits.size + suggestions.size;
  }, [pendingEdits.size, suggestions.size]);

  // Calculate problematic count from all route keys
  const problematicCount = React.useMemo(() => {
    let count = 0;
    allRouteKeys.forEach((tk) => {
      if (isProblematicValue(tk.key, tk.currentValue)) {
        count++;
      }
    });
    return count;
  }, [allRouteKeys]);

  // Update hiddenKeys when showProblematicOnly toggle changes
  const handleSetShowProblematicOnly = useCallback((value: boolean) => {
    setShowProblematicOnly(value);
    
    const newHiddenKeys = new Map<string, LocalizationKey>();
    allRouteKeys.forEach((tk, compositeKey) => {
      // Skip keys that are already visible
      if (visibleKeys.has(compositeKey)) return;
      
      // Apply problematic filter if enabled
      if (!value || isProblematicValue(tk.key, tk.currentValue)) {
        newHiddenKeys.set(compositeKey, tk);
      }
    });
    setHiddenKeys(newHiddenKeys);
  }, [allRouteKeys, visibleKeys]);

  return (
    <LocalizationEditContext.Provider
      value={{
        isEditMode,
        currentScope,
        visibleKeys,
        hiddenKeys,
        pendingEdits,
        suggestions,
        selectedModel,
        isRegenerating,
        regeneratingKey,
        isSaving,
        isLoadingDb,
        pageRoute: location.pathname,
        pageTitle,
        showProblematicOnly,
        setShowProblematicOnly: handleSetShowProblematicOnly,
        problematicCount,
        
        // Locale selector
        editLocale,
        setEditLocale,
        availableLocales,
        
        enterEditMode,
        exitEditMode,
        setCurrentScope,
        registerKey,
        unregisterKey,
        registerHiddenKey,
        setPendingEdit,
        clearPendingEdit,
        setSelectedModel: handleSetSelectedModel,
        regeneratePage,
        regenerateSingleKey,
        applySuggestion,
        rejectSuggestion,
        applyAllSuggestions,
        rejectAllSuggestions,
        saveAllChanges,
        discardChanges,
        getPendingCount,
        getDbValue,
      }}
    >
      {children}
    </LocalizationEditContext.Provider>
  );
}

export function useLocalizationEdit() {
  const context = useContext(LocalizationEditContext);
  if (!context) {
    throw new Error('useLocalizationEdit must be used within a LocalizationEditProvider');
  }
  return context;
}
